import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Monogram } from "@/components/site/Logo";
import { getService, SERVICES } from "@/data/services";

const EASE = [0.16, 1, 0.3, 1];

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-quantum-bg text-quantum-text">
      {/* Top bar */}
      <header className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-24 py-5 backdrop-blur-xl bg-quantum-bg/70 border-b border-white/10">
        <Link to="/#services" data-testid="detail-back" className="flex items-center gap-2 text-quantum-muted hover:text-quantum-gold transition-colors">
          <ArrowLeft size={18} /> <span className="text-sm">All services</span>
        </Link>
        <Link to="/" className="flex items-center gap-2" data-testid="detail-logo">
          <Monogram size={30} />
          <span className="font-serif-q text-xl">Qauntum</span>
        </Link>
        <Link
          to="/#contact"
          data-testid="detail-header-cta"
          className="text-sm px-5 py-2.5 bg-quantum-gold text-black hover:bg-white transition-colors duration-300"
        >
          Enquire
        </Link>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          {service.image ? (
            <img src={service.image} alt={service.title} className="h-full w-full object-cover opacity-25" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-end pr-6 md:pr-16 opacity-[0.06]">
              <Icon size={420} strokeWidth={0.5} className="text-quantum-gold" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-quantum-bg via-quantum-bg/70 to-quantum-bg/40" />
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-24 pb-20 md:pt-32 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="grid place-items-center h-16 w-16 rounded-full border border-quantum-gold/40 text-quantum-gold">
                <Icon size={26} strokeWidth={1.5} />
              </span>
              <span className="font-serif-q text-quantum-gold text-2xl">{service.num}</span>
            </div>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-quantum-gold mb-5">
              {service.tagline}
            </p>
            <h1 className="font-serif-q font-light text-5xl md:text-7xl lg:text-[6rem] leading-[0.95] tracking-tight max-w-4xl">
              {service.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-quantum-gold">Overview</p>
          </div>
          <div className="lg:col-span-8 lg:col-start-4 space-y-6">
            {service.overview.map((p, idx) => (
              <p key={idx} className="text-xl md:text-2xl text-quantum-text/90 font-light leading-relaxed font-serif-q">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24 bg-quantum-bg2 border-y border-white/10">
        <div className="mb-12 md:mb-16 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-quantum-gold mb-5">What&apos;s included</p>
          <h2 className="font-serif-q text-4xl md:text-5xl font-light">Our {service.title.toLowerCase()}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {service.offerings.map((o, idx) => (
            <motion.div
              key={o.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: EASE, delay: (idx % 2) * 0.08 }}
              data-testid={`offering-${idx}`}
              className="group border border-white/10 bg-quantum-bg p-8 md:p-10 hover:border-quantum-gold/40 transition-colors duration-500"
            >
              <span className="mb-6 grid place-items-center h-10 w-10 rounded-full bg-quantum-gold/15 text-quantum-gold">
                <Check size={16} />
              </span>
              <h3 className="font-serif-q text-2xl md:text-3xl text-quantum-text mb-3 font-light">{o.title}</h3>
              <p className="text-quantum-muted leading-relaxed">{o.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14">
          <Link
            to={`/?service=${encodeURIComponent(service.title)}#contact`}
            data-testid="detail-cta"
            className="group inline-flex items-center gap-3 bg-quantum-gold text-black px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-white transition-colors duration-300"
          >
            Enquire about {service.title}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Other services */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <h2 className="font-serif-q text-3xl md:text-4xl font-light mb-10">Explore other services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {others.map((o) => {
            const OIcon = o.icon;
            return (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                data-testid={`other-service-${o.slug}`}
                className="group border border-white/10 bg-quantum-bg2 p-8 hover:border-quantum-gold/40 transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="grid place-items-center h-11 w-11 rounded-full border border-white/15 text-quantum-gold group-hover:border-quantum-gold transition-colors">
                    <OIcon size={18} strokeWidth={1.5} />
                  </span>
                  <ArrowUpRight className="text-quantum-muted group-hover:text-quantum-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" size={22} />
                </div>
                <h3 className="font-serif-q text-2xl text-quantum-text font-light">{o.title}</h3>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
